import React, { ReactNode, createContext, useReducer } from "react";

// eslint-disable-next-line import/no-named-as-default
import produce from "immer";

import { ScenarioField } from "components/stick-data-management/types/field";

import { Scenario, Stick } from "../models/scenario";
import { StickRow } from "../models/stickData";

export interface PadScenarioContextState {
  scenario: Scenario;
  scenarioList: Scenario[];
  sticks: Stick[];
  checkedScenarios: Scenario[];
  scenarioFields: ScenarioField[];
  stickData: StickRow[];
  isLoadingStickData: boolean;
  stickDataError: Error | null;
}

type PadScenarioContextAction = { type: "SET_SCENARIO"; payload: Scenario };
type ScenarioListAction = { type: "SET_SCENARIO_LIST"; payload: Scenario[] };
type SticksAction = { type: "SET_STICKS"; scenarioId: string; payload: Stick[] };
type ScenarioFieldsAction = { type: "SET_SCENARIO_FIELDS"; payload: ScenarioField[] };
type StickDataAction = { type: "SET_STICK_DATA"; payload: StickRow[] };
type StickDataLoadingAction = { type: "SET_STICK_DATA_LOADING"; payload: boolean };
type StickDataErrorAction = { type: "SET_STICK_DATA_ERROR"; payload: Error | null };
type CheckedScenariosAction = { type: "SET_CHECKED_SCENARIOS"; payload: Scenario[] };

export type PadActions =
  | PadScenarioContextAction
  | ScenarioListAction
  | SticksAction
  | ScenarioFieldsAction
  | StickDataAction
  | StickDataLoadingAction
  | StickDataErrorAction
  | CheckedScenariosAction;

// Reducer function to handle state updates
function reducer(state: PadScenarioContextState, action: PadActions) {
  return produce(state, (draft) => {
    switch (action.type) {
      case "SET_SCENARIO":
        // Clear in the same update as the selection so the previous scenario's
        // sticks are never shown (or deleted) under the new scenario's id.
        // List refreshes re-select the same scenario and must keep them.
        if (action.payload?._id?.$oid !== draft.scenario?._id?.$oid) {
          draft.sticks = [];
        }
        draft.scenario = action.payload;
        break;
      case "SET_SCENARIO_LIST":
        draft.scenarioList = action.payload;
        break;
      case "SET_STICKS":
        if (action.scenarioId === draft.scenario?._id?.$oid) {
          draft.sticks = action.payload;
        }
        break;
      case "SET_SCENARIO_FIELDS":
        draft.scenarioFields = action.payload;
        break;
      case "SET_STICK_DATA":
        draft.stickData = action.payload;
        break;
      case "SET_STICK_DATA_LOADING":
        draft.isLoadingStickData = action.payload;
        break;
      case "SET_STICK_DATA_ERROR":
        draft.stickDataError = action.payload;
        break;
      case "SET_CHECKED_SCENARIOS":
        draft.checkedScenarios = action.payload;
        break;
      default:
        break;
    }
  });
}

// Create the context with a default value
export const PadScenarioContext = createContext<
  | {
      state: PadScenarioContextState;
      dispatch: React.Dispatch<PadActions>;
    }
  | undefined
>(undefined);

// Create a provider component
export const PadScenarioProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    scenario: {} as Scenario,
    scenarioList: [],
    sticks: [],
    checkedScenarios: [],
    scenarioFields: [],
    stickData: [],
    isLoadingStickData: false,
    stickDataError: null
  });

  return (
    <PadScenarioContext.Provider value={{ state, dispatch }}>
      {children}
    </PadScenarioContext.Provider>
  );
};
